/**
 * The client half of the shared event-log core: a log's spec bound to the coverage engine.
 *
 * A binding is still written per log; this is what every binding repeats. The spec names the log; the scope names
 * which slice of it one sync walks (a path-level selector such as a stream, the lens values queried, and the shape
 * read); the key the coverage is stored under derives from both, so two logs, or two slices of one, never share spans.
 */

import type { LensRecord, LensValues } from "@storyboard/event-log/lens";
import type { EventLogQuery } from "@storyboard/event-log/query";
import type { ShapeRecord, ShapeValues } from "@storyboard/event-log/shape";
import type { EventLogSpec } from "@storyboard/event-log/spec";
import { coverageKey } from "@/models/sync/cursorStore";
import {
  COLD_BOOT_LADDER_HOURS,
  type CoverageStore,
  type CoverageSyncEvent,
  createCoverageSync,
} from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";

/** The shared query of one delta page: a delta always has its floor. */
export type EventLogDeltaQuery = EventLogQuery & { since: string };

/** The selector a log with no path-level one is keyed under: the whole workspace. */
export const WORKSPACE_SELECTOR = "workspace";

/**
 * What narrows one coverage key: the path-level selector a route has (a stream, an acknowledgement subject), the
 * lens values the walk queries with, and the shape it reads each row in.
 *
 * A lens left out reads the whole log on that axis, so it adds nothing to the key. Holding a window in one shape says
 * nothing about it in another, so each shape value in the scope is in the key. A shape with a default always holds a
 * value, so it is always in the key; an optional shape left out is the route's read without it, and adds nothing, so
 * the two reads never share spans. A log with no shape parameters has an empty `shape`.
 */
export type EventLogScope<L extends LensRecord, S extends ShapeRecord> = {
  selector?: string;
  lenses: Partial<LensValues<L>>;
  shape: ShapeValues<S>;
};

/** One parameter value as the key carries it: scalars as text, lists joined by commas. */
function paramValueKey(value: unknown): string {
  return Array.isArray(value) ? value.map(String).join(",") : String(value);
}

/**
 * The registry key a scope's coverage lives under.
 *
 * `${name}:${selector}:coverage` for a scope with no lens values on a log with no shape, which is byte for byte the
 * key every log stored under before the specs existed, so no stored state moves. Lens values follow the selector in
 * the spec's declaration order, as `name=value`, then the shape values the same way, so a `kinds` slice, a membership
 * slice or an English-only read each gets its own spans.
 */
export function coverageKeyFor<L extends LensRecord, S extends ShapeRecord>(
  spec: EventLogSpec<string, L, S>,
  scope: EventLogScope<L, S>,
): string {
  const parts: string[] = [scope.selector ?? WORKSPACE_SELECTOR];
  for (const name of Object.keys(spec.lenses)) {
    const value = (scope.lenses as Record<string, unknown>)[name];
    if (value !== undefined) parts.push(`${name}=${paramValueKey(value)}`);
  }
  for (const name of Object.keys(spec.shape)) {
    const value = (scope.shape as Record<string, unknown>)[name];
    if (value !== undefined) parts.push(`${name}=${paramValueKey(value)}`);
  }
  return coverageKey(spec.name, parts.join(":"));
}

/**
 * The collaborators a binding reads and writes through; production binds the transport, the registry and the store.
 *
 * @template L - The spec's lenses.
 * @template S - The spec's shape parameters.
 * @template TPage - One page of the log as the route serves it.
 */
export type EventLogBindingDeps<
  L extends LensRecord,
  S extends ShapeRecord,
  TPage extends { cursor?: string | null },
> = {
  /** How far back the log is held, from the server's clock. */
  depth: { hours: number };
  /** Rung edges in hours before now; see `rungsOf`. Defaults to {@link COLD_BOOT_LADDER_HOURS}. */
  ladderHours?: readonly number[];
  /** Pages one gap fill may take before it stops, keeping its progress for the next sync to resume. */
  maxPages: number;
  /** Rows asked for per page. Defaults to the spec's cap, the most the route serves; a log polled often asks for less. */
  limit?: number;
  coverage: CoverageStore;
  /**
   * One delta page of the scope: the shared query the engine asks for, which the binding sends with the scope's lens
   * and shape values.
   *
   * The scope's values are the handler's, as `LensValues` and `ShapeValues` type them; the binding puts them on the
   * wire the way the route reads them (a list lens as a comma-joined string).
   */
  fetchPage: (ctx: SyncContext, scope: EventLogScope<L, S>, query: EventLogDeltaQuery) => Promise<TPage>;
  applyPage: (page: TPage, scope: EventLogScope<L, S>) => Promise<void> | void;
  /** The newest `createdAt` on a page; undefined for an empty page. */
  newestCreatedAt: (page: TPage) => Date | undefined;
  /** The server's clock as the client estimates it from every response's `Date` header. */
  now: () => Date;
  /** A recoverable condition worth a reliability event, already prefixed with the log's name. */
  report: (event: string, attributes: Record<string, unknown>) => void;
};

export type EventLogBinding<L extends LensRecord, S extends ShapeRecord> = {
  /** Brings the scope's coverage up to `[now − depth, now − lag]`; see `CoverageSync.sync`. */
  sync: (ctx: SyncContext, scope: EventLogScope<L, S>) => Promise<void>;
  /** Resolves once no background fill is running for the scope; see `CoverageSync.backfilled`. */
  backfilled: (ctx: SyncContext, scope: EventLogScope<L, S>) => Promise<void>;
};

/**
 * Binds a spec to the coverage engine.
 *
 * The engine keys its locks and spans by a name; here the name is the scope's coverage key, so one engine serves every
 * scope of the log. Telemetry events are `${spec.name}.${event}`, with the scope's key as `scope`.
 *
 * @param spec - The log.
 * @param deps - How a page is fetched and landed, and where coverage is kept.
 */
export function bindEventLog<L extends LensRecord, S extends ShapeRecord, TPage extends { cursor?: string | null }>(
  spec: EventLogSpec<string, L, S>,
  deps: EventLogBindingDeps<L, S, TPage>,
): EventLogBinding<L, S> {
  /** One page as the engine sees it: the route's page with the scope it was fetched for. */
  type ScopedPage = { page: TPage; scope: EventLogScope<L, S>; cursor?: string | null };

  /** The scope behind each key, for the engine's fetch, which sees only the key. */
  const scopes = new Map<string, EventLogScope<L, S>>();

  const scopeOf = (key: string): EventLogScope<L, S> => {
    const scope = scopes.get(key);
    if (scope === undefined) throw new Error(`${spec.name}: no scope is bound to ${key}`);
    return scope;
  };

  const engine = createCoverageSync<string, ScopedPage>({
    depth: deps.depth,
    ladderHours: deps.ladderHours ?? COLD_BOOT_LADDER_HOURS,
    maxPages: deps.maxPages,
    store: deps.coverage,
    keyOf: (key) => key,
    fetchPage: async (ctx, key, since, cursor, until) => {
      const scope = scopeOf(key);
      const page = await deps.fetchPage(
        ctx,
        scope,
        eventLogPageQuery(since, cursor, deps.limit ?? spec.limits.maxLimit, until),
      );
      return { page, scope, ...(page.cursor === undefined ? {} : { cursor: page.cursor }) };
    },
    applyPage: ({ page, scope }) => deps.applyPage(page, scope),
    newestCreatedAt: ({ page }) => deps.newestCreatedAt(page),
    now: deps.now,
    report: (event: CoverageSyncEvent, key, attributes) =>
      deps.report(`${spec.name}.${event}`, { scope: key, ...attributes }),
  });

  return {
    sync: (ctx, scope) => {
      const key = coverageKeyFor(spec, scope);
      scopes.set(key, scope);
      return engine.sync(ctx, key);
    },
    backfilled: (ctx, scope) => engine.backfilled(ctx, coverageKeyFor(spec, scope)),
  };
}

/**
 * The shared query of one delta page: `since` and `limit`, with the cursor only when continuing a page, and `until`
 * only when the gap has an upper edge.
 *
 * @param since - The floor the page reads from.
 * @param cursor - The previous page's cursor, if any.
 * @param limit - Rows per page; the route clamps it to its cap.
 * @param until - The gap's upper edge, inclusive; absent for a gap open to the head.
 */
export function eventLogPageQuery(
  since: Date,
  cursor: string | undefined,
  limit: number,
  until?: Date,
): EventLogDeltaQuery {
  return {
    since: since.toISOString(),
    limit,
    ...(cursor === undefined ? {} : { cursor }),
    ...(until === undefined ? {} : { until: until.toISOString() }),
  };
}
